import { ThemeToggle } from '@/components/ThemeToggle';
import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

// 52px icon-only rail (Palantir Foundry navigator pattern).
// Active item: 2px primary stripe on left + soft blue tint.
// Bottom: per-type counts in mono fg-tertiary.

// `code` is a language-agnostic 3-letter ID; only `labelKey` is translated.
const NAV: Array<{
  to: string;
  labelKey: string;
  code: string;
  icon: keyof typeof ICONS;
  matches?: string[];
}> = [
  {
    to: '/terminal',
    labelKey: 'shell.nav.terminal',
    code: 'TRM',
    icon: 'discover',
    matches: ['/terminal', '/brief'],
  },
  { to: '/horizon', labelKey: 'shell.nav.horizon', code: 'HZN', icon: 'pipeline' },
  { to: '/intel', labelKey: 'shell.nav.intel', code: 'INT', icon: 'world' },
  { to: '/ops', labelKey: 'shell.nav.ops', code: 'OPS', icon: 'case' },
  { to: '/audit', labelKey: 'shell.nav.audit', code: 'AUD', icon: 'admin' },
];

export function NavRail() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  return (
    <nav
      className="shell-nav"
      style={{
        width: 52,
        flexShrink: 0,
        background: 'var(--bg-pane)',
        borderRight: '1px solid var(--bd)',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 6,
      }}
    >
      <div
        className="shell-nav-items"
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        {NAV.map((it) => {
          const matches = it.matches ?? [it.to];
          const active = matches.some((m) => pathname === m || pathname.startsWith(`${m}/`));
          const label = t(it.labelKey);
          return (
            <Link
              key={it.to}
              to={it.to}
              title={`${it.code} · ${label}`}
              className={`shell-nav-item${active ? ' active' : ''}`}
              style={{
                position: 'relative',
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: active ? 'var(--primary)' : 'var(--fg-2)',
                background: active ? 'var(--primary-soft)' : 'transparent',
                boxShadow: active ? 'inset 2px 0 0 var(--primary)' : 'none',
                textDecoration: 'none',
              }}
            >
              {ICONS[it.icon]}
              <span className="shell-nav-label" style={{ display: 'none' }}>
                {label}
              </span>
            </Link>
          );
        })}
      </div>

      <ThemeToggle />
    </nav>
  );
}

// 16px line icons, single weight, no fills.
const I = (path: string) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.25"
    strokeLinecap="square"
    strokeLinejoin="miter"
    aria-hidden
  >
    <path d={path} />
  </svg>
);

const ICONS = {
  discover: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      aria-hidden
    >
      <rect x="2" y="2" width="12" height="12" />
      <path d="M2 6h12M6 2v12" />
    </svg>
  ),
  world: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      aria-hidden
    >
      <circle cx="8" cy="8" r="6" />
      <path d="M2 8h12M8 2c2 2 2 10 0 12M8 2c-2 2-2 10 0 12" />
    </svg>
  ),
  pipeline: I('M2 13V3 M2 13h11 M4 11l3-3 2 2 4-5'),
  case: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      aria-hidden
    >
      <path d="M2 2h5v5H2zM9 2h5v5H9zM2 9h5v5H9zM9 9h5v5H9z" />
    </svg>
  ),
  watch: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      aria-hidden
    >
      <circle cx="8" cy="8" r="3" />
      <path d="M1 8c2-3 4-4 7-4s5 1 7 4c-2 3-4 4-7 4s-5-1-7-4z" />
    </svg>
  ),
  agent: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      aria-hidden
    >
      <circle cx="3" cy="3" r="1.5" />
      <circle cx="13" cy="8" r="1.5" />
      <circle cx="3" cy="13" r="1.5" />
      <path d="M4 3h6c2 0 2 2 2 5 0 3 0 5-2 5H4" />
    </svg>
  ),
  alert: I('M8 2l6 11H2zM8 7v3M8 12v.5'),
  admin: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      aria-hidden
    >
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.4 1.4M11.6 11.6L13 13M3 13l1.4-1.4M11.6 4.4L13 3" />
    </svg>
  ),
} as const;
